import { createContext, ComponentChildren } from 'preact';
import { useContext, useEffect, useState, useCallback } from 'preact/hooks';
import { sharedCookieDomain } from '../cookie-domain.ts';

export type Theme = 'porcelain' | 'graphite';

interface ThemeCtx {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeCtx | null>(null);

// Theme persists in a cookie scoped to the registrable domain that the `app.`,
// `www.`, and apex hosts share (e.g. `movegoalpost.com`), so a choice made on
// one carries to the others. The inline theme bootstrap in index.html reads the
// same cookie before first paint to avoid a flash of the wrong theme; keep the
// two in sync.
const COOKIE_NAME = 'gp_theme';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // one year

function readThemeCookie(): Theme | null {
  try {
    const prefix = `${COOKIE_NAME}=`;
    const value = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(prefix))
      ?.slice(prefix.length);
    if (value === 'porcelain' || value === 'graphite') return value;
  } catch {
    // document.cookie can throw in sandboxed contexts — fall through.
  }
  return null;
}

function writeThemeCookie(theme: Theme): void {
  try {
    const parts = [
      `${COOKIE_NAME}=${theme}`,
      'Path=/',
      `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
      'SameSite=Lax'
    ];
    const domain = sharedCookieDomain();
    if (domain) parts.push(`Domain=${domain}`);
    if (window.location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  } catch {
    // Ignore cookie write failures; the in-memory class update still applies.
  }
}

function readInitial(): Theme {
  const stored = readThemeCookie();
  if (stored) return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'graphite'
    : 'porcelain';
}

export function ThemeProvider({ children }: { children: ComponentChildren }) {
  const [theme, setThemeState] = useState<Theme>(readInitial);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('porcelain', 'graphite');
    root.classList.add(theme);
    if (!root.classList.contains('palette')) root.classList.add('palette');
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    writeThemeCookie(t);
    setThemeState(t);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'porcelain' ? 'graphite' : 'porcelain');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
